import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import toast from 'react-hot-toast';
import LOGO from '../assets/logo.png';
import GlobalSearch from './GlobalSearch';
import NotificationBell from './NotificationBell';

const ROLE_LABELS = {
  care_rep:'Customer Care Rep', ict_staff:'ICT Staff',
  ict_manager:'ICT Manager', branch_manager:'Branch Manager',
  finance_officer:'Finance Officer', super_admin:'Super Admin',
  sa_initiator:'SA Initiator', sa_approver:'SA Approver',
};

const PAGE_TITLES = {
  '/':'Dashboard', '/tickets':'All Tickets', '/tickets/new':'Lodge a Ticket',
  '/users':'User Management', '/reports':'Reports & Analytics',
  '/audit':'Audit Log', '/sa':'Salary Advance', '/sa/new':'New SA Request',
  '/sa/reports':'SA Reports',
};

const EyeIcon = ({ show, toggle }) => (
  <span onClick={toggle} style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', cursor:'pointer', fontSize:16, color:'var(--muted)', userSelect:'none' }}>
    {show ? '🙈' : '👁️'}
  </span>
);

export default function Layout({ children }) {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password:'', new_password:'', confirm_password:'' });
  const [pwLoading, setPwLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const initials = user?.full_name?.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase();
  const title = PAGE_TITLES[location.pathname] ||
    (location.pathname.startsWith('/tickets/') ? 'Ticket Detail' :
     location.pathname.startsWith('/sa/') ? 'SA Request Detail' : 'OMIYE MFB HelpDesk');

  const navLinks = [
    { to:'/', label:'Dashboard', icon:'📊', exact:true, roles:['care_rep','ict_staff','ict_manager','branch_manager','finance_officer','super_admin'] },
    { to:'/sa', label:'💰 Salary Advance', icon:'💰', roles:['sa_initiator','sa_approver','branch_manager','ict_manager','super_admin'] },
    { to:'/sa/reports', label:'SA Reports', icon:'📊', roles:['sa_initiator','sa_approver','branch_manager','ict_manager','super_admin'] },
    { to:'/tickets', label:'My Tickets', icon:'🎫', roles:['care_rep','branch_manager'] },
    { to:'/tickets', label:'All Tickets', icon:'🎫', roles:['ict_staff','ict_manager','finance_officer','super_admin'] },
    { to:'/tickets/new', label:'Lodge Ticket', icon:'✏️', roles:['care_rep','ict_staff','ict_manager','finance_officer','super_admin'] },
    { to:'/reports', label:'Reports', icon:'📈', roles:['ict_manager','super_admin'] },
    { to:'/audit', label:'Audit Log', icon:'📋', roles:['ict_manager','super_admin'] },
    { to:'/login-history', label:'Login History', icon:'🔐', roles:['super_admin'] },
    { to:'/users', label:'User Management', icon:'👥', roles:['super_admin'] },
  ].filter(n => n.roles.includes(user?.role));

  const handleChangePassword = async () => {
    if (!pwForm.current_password || !pwForm.new_password || !pwForm.confirm_password)
      return toast.error('Please fill all fields');
    if (pwForm.new_password !== pwForm.confirm_password)
      return toast.error('New passwords do not match');
    if (pwForm.new_password.length < 6)
      return toast.error('New password must be at least 6 characters');
    if (pwForm.new_password === pwForm.current_password)
      return toast.error('New password cannot be same as current');
    setPwLoading(true);
    try {
      await api.put('/api/auth/change-password', { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success('Password changed successfully!');
      setShowPasswordModal(false);
      setPwForm({ current_password:'', new_password:'', confirm_password:'' });
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to change password'); }
    finally { setPwLoading(false); }
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPwForm({ current_password:'', new_password:'', confirm_password:'' });
    setShowCurrent(false); setShowNew(false); setShowConfirm(false);
  };

  return (
    <div className="app-layout">
      {/* Mobile overlay */}
      {sidebarOpen && <div onClick={() => setSidebarOpen(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:999 }} />}

      <aside className={`sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="logo-area">
          <img src={LOGO} alt="OMIYE MFB" className="logo-img" />
          <div className="logo-name">OMIYE MFB</div>
          <div className="logo-sub">Internal Support Portal</div>
        </div>

        <nav className="nav-wrap">
          {navLinks.map(link => (
            <NavLink key={link.to+link.label} to={link.to} end={link.exact}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
              <span>{link.icon}</span>
              <span>{link.label}</span>
            </NavLink>
          ))}
        </nav>

        <div style={{ position:'relative' }}>
          <div className="user-pill" style={{ cursor:'pointer' }} onClick={() => setShowUserMenu(!showUserMenu)}>
            <div className="avatar">{initials}</div>
            <div style={{ flex:1, minWidth:0 }}>
              <div className="user-name-pill">{user?.full_name}</div>
              <div className="user-role-pill">{ROLE_LABELS[user?.role]}</div>
            </div>
            <div style={{ color:'rgba(255,255,255,0.4)', fontSize:10 }}>▲</div>
          </div>

          {showUserMenu && (
            <div style={{ position:'absolute', bottom:'100%', left:0, right:0, background:'#0a4a75', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, overflow:'hidden', zIndex:200 }}>
              <div style={{ padding:'10px 14px', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ color:'#fff', fontSize:12, fontWeight:500 }}>{user?.full_name}</div>
                <div style={{ color:'rgba(255,255,255,0.4)', fontSize:11 }}>{user?.email}</div>
              </div>
              <NavLink to="/profile" onClick={() => setShowUserMenu(false)}
                style={{ display:'block', padding:'10px 14px', color:'rgba(255,255,255,0.7)', fontSize:13, textDecoration:'none' }}>
                👤 My Profile
              </NavLink>
              <div style={{ padding:'10px 14px', color:'rgba(255,255,255,0.7)', fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}
                onClick={() => { setShowPasswordModal(true); setShowUserMenu(false); }}>
                🔑 Change Password
              </div>
              <div style={{ padding:'10px 14px', color:'rgba(255,100,100,0.9)', fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', gap:8, borderTop:'1px solid rgba(255,255,255,0.08)' }}
                onClick={logout}>
                🚪 Sign Out
              </div>
            </div>
          )}
        </div>
      </aside>

      <div className="main-area">
        <header className="topbar">
          {/* Mobile hamburger */}
          <button onClick={() => setSidebarOpen(true)}
            style={{ display:'none', flexDirection:'column', gap:4, cursor:'pointer', padding:6, border:'none', background:'none', marginRight:8 }}
            className="hamburger-btn">
            <span style={{ display:'block', width:22, height:2, background:'var(--sky)', borderRadius:2 }} />
            <span style={{ display:'block', width:22, height:2, background:'var(--sky)', borderRadius:2 }} />
            <span style={{ display:'block', width:22, height:2, background:'var(--sky)', borderRadius:2 }} />
          </button>
          <div className="topbar-title">{title}</div>
          <GlobalSearch />
          <div className="topbar-actions">
            <NotificationBell />
            {can('care_rep','ict_manager','super_admin','finance_officer','ict_staff') && (
              <button className="btn btn-orange btn-sm" onClick={() => navigate('/tickets/new')}>+ New Ticket</button>
            )}
            {can('sa_initiator') && (
              <button className="btn btn-primary btn-sm" onClick={() => navigate('/sa/new')}>+ SA Request</button>
            )}
            <button className="btn btn-ghost btn-sm" onClick={logout}>🚪 Sign Out</button>
          </div>
        </header>
        <main className="page-content">
          {children}
        </main>
        <footer style={{ padding:'12px 24px', textAlign:'center', fontSize:11, color:'var(--text3)', borderTop:'1px solid var(--border)', background:'var(--white)' }}>
          Powered by OMIYE MFB ICT · © {new Date().getFullYear()} OMIYE Microfinance Bank
        </footer>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'#fff', borderRadius:12, padding:28, width:'100%', maxWidth:420 }}>
            <h3 style={{ fontFamily:'Sora', fontSize:16, marginBottom:6 }}>🔑 Change Password</h3>
            <p style={{ fontSize:12, color:'var(--muted)', marginBottom:20 }}>Choose a strong password with at least 6 characters.</p>
            {[
              { key:'current_password', label:'Current Password', show:showCurrent, toggle:() => setShowCurrent(!showCurrent) },
              { key:'new_password', label:'New Password', show:showNew, toggle:() => setShowNew(!showNew) },
              { key:'confirm_password', label:'Confirm New Password', show:showConfirm, toggle:() => setShowConfirm(!showConfirm) },
            ].map(f => (
              <div className="form-group" key={f.key}>
                <label className="form-label">{f.label}</label>
                <div style={{ position:'relative' }}>
                  <input className="form-input" type={f.show ? 'text' : 'password'} style={{ paddingRight:40 }}
                    placeholder={f.key === 'current_password' ? 'Enter current password' : f.key === 'new_password' ? 'Min 6 characters' : 'Re-enter new password'}
                    value={pwForm[f.key]} onChange={e => setPwForm({...pwForm, [f.key]:e.target.value})} />
                  <EyeIcon show={f.show} toggle={f.toggle} />
                </div>
              </div>
            ))}
            <div style={{ display:'flex', gap:10, marginTop:6 }}>
              <button className="btn btn-primary" onClick={handleChangePassword} disabled={pwLoading}>
                {pwLoading ? 'Saving…' : '💾 Save New Password'}
              </button>
              <button className="btn btn-ghost" onClick={closePasswordModal}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
